import React, { useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Button } from "./Buttons";
import { useUserProfile } from "../hooks/useUserProfile";
import LogoLink from "./Navbar/LogoLink";
import ThemeToggle from "./Navbar/ThemeToggle";
import SearchBar from "./Navbar/SearchBar";
import NavLinks from "./Navbar/NavLinks";
import UserMenu from "./Navbar/UserMenu";
import CreateMenu from "./Navbar/CreateMenu";
import CommandPalette from "./Navbar/CommandPalette";
import ExploreMenu from "./Navbar/ExploreMenu";
import MobileDrawer from "./Navbar/MobileDrawer";
import ChallengesMenu from "./Navbar/ChallengesMenu";
const Navbar = () => {
  const { user, setUser, setActive, active } = useAppContext();
  const { profile } = useUserProfile(user?.uid);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    signOut(auth).then(() => {
      setUser(null);
      setActive("login");
      navigate("/auth");
    });
  };
  const userId = user?.uid;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [exploreMenuAnchor, setExploreMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [challengesMenuAnchor, setChallengesMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const exploreButtonRef = useRef<HTMLButtonElement | null>(null);
  const challengesButtonRef = useRef<HTMLButtonElement | null>(null);
  const cmdPaletteAnchorRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const open = Boolean(anchorEl);
  const createMenuOpen = Boolean(createMenuAnchor);
  const moreOpen = Boolean(moreMenuAnchor);
  const exploreMenuOpen = Boolean(exploreMenuAnchor);
  const challengesMenuOpen = Boolean(challengesMenuAnchor);
  const [cmdPaletteAnchor, setCmdPaletteAnchor] = React.useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = React.useState<string>("");

  const handleExploreMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setExploreMenuAnchor(e.currentTarget);
  };

  const handleExploreMenuClose = () => {
    setExploreMenuAnchor(null);
  };

  const handleChallengesMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setChallengesMenuAnchor(e.currentTarget);
  };

  const handleChallengesMenuClose = () => {
    setChallengesMenuAnchor(null);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleCreateMenuOpen = (e: React.MouseEvent<HTMLElement>) => setCreateMenuAnchor(e.currentTarget);
  const handleCreateMenuClose = () => setCreateMenuAnchor(null);
  const handleMoreOpen = (e: React.MouseEvent<HTMLElement>) => setMoreMenuAnchor(e.currentTarget);
  const handleMoreClose = () => setMoreMenuAnchor(null);
  const handleCmdPaletteOpen = () => setCmdPaletteAnchor(cmdPaletteAnchorRef.current);
  const handleCmdPaletteClose = () => setCmdPaletteAnchor(null);

  // Keep the top-level nav minimal to reduce clutter
  const navItems = [
    { label: "Explore", path: "/explore", key: "explore" },
  ];

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = (searchValue || "").trim();
    if (!q) {
      navigate("/explore");
      setActive("explore");
      return;
    }
    const params = new URLSearchParams();
    // Use title-only search flag for Explore page
    params.set("title", q);
    navigate(`/explore?${params.toString()}`);
    setActive("explore");
  };
  useEffect(() => {
    const path = location.pathname;

    // Check for story routes
    if (path.startsWith("/create-story")) {
      if (active !== "create-story") setActive("create-story");
      return;
    }
    if (path.startsWith("/story/") && path.includes("/chapter/")) {
      // Chapter details page
      return;
    }
    if (path.startsWith("/story/")) {
      // Story details page
      return;
    }
    if (path.startsWith("/manage-story")) {
      if (active !== "create-story") setActive("create-story");
      return;
    }
    // Check for Challenges/Ink Masters routes
    if (path.startsWith("/challenges/ink-masters")) {
      if (active !== "ink-masters") setActive("ink-masters");
      return;
    }

    const match = navItems.find((item) => item.path === path);
    if (match) {
      if (active !== match.key) setActive(match.key);
    } else {
      // If the current path isn't in navItems, clear the active state
      if (active !== null) setActive(null);
    }
  }, [location.pathname, active]);

  // Keyboard shortcuts: '/' focuses search, Cmd/Ctrl+K opens command palette
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTypingElement = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.getAttribute("contenteditable") === "true");
      if (!isTypingElement && e.key === "/") {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        } else {
          navigate("/explore");
          setActive("explore");
        }
      }
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (cmdOrCtrl && (e.key.toLowerCase() === "k")) {
        e.preventDefault();
        setMoreMenuAnchor(null);
        setCreateMenuAnchor(null);
        handleCmdPaletteOpen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate, setActive]);

  const renderNavButtons = () => (
    <NavLinks
      active={active}
      setActive={setActive}
      navItems={navItems}
      handleCreateMenuOpen={handleCreateMenuOpen}
      moreMenuAnchor={moreMenuAnchor}
      moreOpen={moreOpen}
      handleMoreOpen={handleMoreOpen}
      handleMoreClose={handleMoreClose}
      onExploreClick={handleExploreMenuOpen}
      exploreButtonRef={exploreButtonRef}
      exploreMenuOpen={exploreMenuOpen}
      onChallengesClick={handleChallengesMenuOpen}
      challengesButtonRef={challengesButtonRef}
      challengesMenuOpen={challengesMenuOpen}
    />
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        borderRadius: 0,
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        border: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.05)",
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Mobile Drawer Toggle */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ color: "text.secondary" }}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          )}
          <LogoLink />
          {!isMobile && (
            <Box sx={{ display: "flex", gap: "2rem" }}>
              {renderNavButtons()}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: 'auto', md: '50%' }, justifyContent: 'flex-end' }}>
          {/* Theme toggle (always visible) */}
          <ThemeToggle />

          {/* Search (only on large screens to reduce clutter) */}
          <SearchBar
            hidden={!isLarge}
                value={searchValue}
            onChange={setSearchValue}
            onSubmit={onSearchSubmit}
                inputRef={searchInputRef}
          />
          {!isLarge && !isMobile && (
            <Tooltip title="Search">
              <IconButton
                onClick={() => { navigate("/explore"); setActive("explore"); }}
                sx={{ color: "text.secondary" }}
                aria-label="Open search"
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}
          {/* Quick create action on mobile: icon + text */}
          {isMobile && (
            <Button
              variant="text"
              size="small"
              component={Link}
              to="/create-story"
              onClick={() => {
                setActive("create-story");
                setCreateMenuAnchor(null);
              }}
              startIcon={<AddIcon />}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.5,
                '& .MuiButton-startIcon': { mr: 0.25 },
              }}
            >
              Write
            </Button>
          )}

          {/* Notifications removed per product state */}

          {/* Show profile avatar only on desktop */}
          {!isMobile && (
            <>
          <UserMenu
            userId={userId}
            user={user || {}}
            profile={profile}
                anchorEl={anchorEl}
                open={open}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
            handleLogout={handleLogout}
            setActive={setActive}
            authUser={user}
                  />

              {!userId && (
            <Button
              variant="outlined"
              component={Link}
              to="/auth"
              onClick={() => setActive("login")}
            >
              Login
            </Button>
              )}
            </>
          )}
        </Box>

        {/* Shared Create Menu (always mounted so it works on mobile + desktop) */}
        <CreateMenu
          userId={userId}
          createMenuAnchor={createMenuAnchor}
          createMenuOpen={createMenuOpen}
          handleCreateMenuClose={handleCreateMenuClose}
          setActive={setActive}
        />
        <CommandPalette
          anchorEl={cmdPaletteAnchor}
          onClose={handleCmdPaletteClose}
          setActive={setActive}
          userId={userId}
          cmdPaletteAnchorRef={cmdPaletteAnchorRef}
          navigate={(path) => navigate(path)}
        />
        
        {/* Explore Category Menu (desktop only) */}
        {!isMobile && (
          <ExploreMenu
            exploreMenuAnchor={exploreMenuAnchor}
            exploreMenuOpen={exploreMenuOpen}
            handleExploreMenuClose={handleExploreMenuClose}
            setActive={setActive}
          />
        )}

        {/* Challenges Menu (desktop only) */}
        {!isMobile && (
          <ChallengesMenu
            challengesMenuAnchor={challengesMenuAnchor}
            challengesMenuOpen={challengesMenuOpen}
            handleChallengesMenuClose={handleChallengesMenuClose}
            setActive={setActive}
          />
        )}

        {/* Mobile Drawer */}
        <MobileDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          userId={userId}
          user={user || {}}
          profile={profile}
          active={active}
          setActive={setActive}
          handleLogout={handleLogout}
          authUser={user}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
